import { TCommonData, THandleClick, TItem, TWidget } from '@libs/types';
import { CoreLayoutView, ORIENTATION_ENUM, TCoreLayoutOptions } from '@core/widgets';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TCarouselData, TConsumerItem } from '../../types/data';
import { getInlineStyles } from '../../utils/styles';
import { CarouselSlide } from './CarouselSlide';
import styled from './carousel.module.css';
import styledNew from './carouseNew.module.css';

type TCursorPosition = {
    xStart: number;
    yStart: number;
};
const percent = 0.01;

const KEY_CONTAINER = 'carouselLayout';

type TCarouselViewProps = {
    widget: TWidget<TCarouselData>;
    uid?: string;
    items?: TConsumerItem[];
    margin?: Parameters<typeof getInlineStyles>[0]['margin'];
    styleName?: string;
    isHide?: boolean;
    onItemClick: THandleClick;
};

export const CarouselView = ({ widget, uid, items: slides = [], margin, styleName, isHide, onItemClick }: TCarouselViewProps) => {
    const parentStyleNames = styleName ? [styleName] : [];

    const cursorPosition = useRef({ xStart: 0, yStart: 0 } as TCursorPosition);
    const [slidesToShow, setSlidesToShow] = useState<number>(1);
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [startX, setStartX] = useState<number>(0);
    const [drag, setDrag] = useState<number>(0);

    const autoPlayTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        const updateSlidesToShow = () => {
            const width = window.innerWidth;
            if (slides.length > 2) {
                if (width >= 708) setSlidesToShow(3);
                else if (width >= 320) setSlidesToShow(2);
                else setSlidesToShow(1);
            } else if (slides.length === 2) {
                setSlidesToShow(2);
            } else {
                setSlidesToShow(1);
            }
        };
        updateSlidesToShow();
        window.addEventListener('resize', updateSlidesToShow);
        return () => window.removeEventListener('resize', updateSlidesToShow);
    }, [slides.length]);

    const displayIndex = Math.min(currentIndex, Math.max(0, slides.length - slidesToShow));

    const goToNext = useCallback(() => {
        setCurrentIndex((prevIndex) => (prevIndex >= slides.length - slidesToShow ? 0 : prevIndex + 1));
    }, [slides.length, slidesToShow]);

    const goToPrev = useCallback(() => {
        setCurrentIndex((prevIndex) => (prevIndex === 0 ? Math.max(0, slides.length - slidesToShow) : prevIndex - 1));
    }, [slides.length, slidesToShow]);

    const goToSlide = (slideIndex: number) => {
        const newIndex = Math.min(slideIndex, Math.max(0, slides.length - slidesToShow));
        setCurrentIndex(newIndex);
    };

    const handleDragStart = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
        e.preventDefault();
        if ((e as React.MouseEvent<HTMLDivElement>)?.clientX) {
            cursorPosition.current.xStart = (e as React.MouseEvent<HTMLDivElement>).clientX;
            cursorPosition.current.yStart = (e as React.MouseEvent<HTMLDivElement>).clientY;
        }
        const startPosition = 'touches' in e ? e.touches[0].clientX : e.clientX;
        setIsDragging(true);
        setStartX(startPosition);
        if (autoPlayTimeoutRef.current) clearTimeout(autoPlayTimeoutRef.current);
    };

    const handleDragMove = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
        if (!isDragging) return;
        const currentX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        setDrag(currentX - startX);
    };

    const handleDragEnd = () => {
        if (!isDragging) return;
        setIsDragging(false);
        const dragThreshold = 50;
        if (drag < -dragThreshold) goToNext();
        else if (drag > dragThreshold) goToPrev();
        setDrag(0);
    };

    const onMouseUp = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, item: TItem) => {
        const deltaX = Math.abs(e.clientX - cursorPosition.current.xStart);
        const deltaY = Math.abs(e.clientY - cursorPosition.current.yStart);
        if (deltaX < window.innerWidth * percent && deltaY < window.innerHeight * percent) {
            onItemClick(uid ?? '', item);
        }
    };

    return (
        <CoreLayoutView
            widget={widget as TWidget<TCommonData, TCoreLayoutOptions>}
            uid={uid}
            isHide={isHide}
            styleName={KEY_CONTAINER}
            parentStyleNames={parentStyleNames}
            className={styledNew.carouselContainer}
            styleProps={{ orientation: ORIENTATION_ENUM.VERTICAL, lineX: 'stretch', lineY: 'start', overflow: 'hidden', margin: margin }}
        >
            <div
                onMouseDown={handleDragStart}
                onMouseMove={handleDragMove}
                onMouseUp={handleDragEnd}
                onMouseLeave={handleDragEnd}
                onTouchStart={handleDragStart}
                onTouchMove={handleDragMove}
                onTouchEnd={handleDragEnd}
            >
                <CoreLayoutView
                    styleProps={{ orientation: ORIENTATION_ENUM.HORIZONTAL, lineX: 'start', lineY: 'stretch' }}
                    style={{
                        transform: `translateX(calc(-${displayIndex * (100 / slidesToShow)}% + ${drag}px))`,
                        transition: isDragging ? 'none' : 'transform 0.5s ease-in-out',
                    }}
                >
                    {slides.map((slide) => (
                        <div
                            key={'slide_' + slide.value}
                            className={styledNew.slideWrapper}
                            style={{ width: `${100 / slidesToShow}%` }}
                            onMouseUp={(e) => onMouseUp(e, slide)}
                        >
                            <CarouselSlide item={slide} styleName={styleName} />
                        </div>
                    ))}
                </CoreLayoutView>
                <div className={styled.carouselDotGroup}>
                    <div className={styledNew.dotGroup}>
                        {slides.map((_, index) => (
                            <button
                                key={index}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    goToSlide(index);
                                }}
                                aria-label={`Go to slide ${index + 1}`}
                                disabled={index >= displayIndex && index < displayIndex + slidesToShow}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </CoreLayoutView>
    );
};
